// The merged money timeline — invoices positive, refunds negative — behind a
// "Show all activity" disclosure so the billing screen is not two near-identical
// money tables stacked. M7e §1.1A.5.
//
// 🔴 GROUPED BY CURRENCY, with a per-group heading, and deliberately NO total
// anywhere. We sell in seven currencies and carry no FX table, so a blended
// figure would be fiction — `sumMinor` in ~/lib/money throws rather than produce
// one, and this screen simply never asks for it.

import { useT } from '@fajrarabi/i18n/react';
import { Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Money } from '~/components/Money';
import { listTransactions, type Transaction } from '~/lib/billing';
import { groupByCurrency } from '~/lib/money';

export function TransactionsList() {
  const t = useT();
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['billing', 'transactions'],
    queryFn: ({ signal }) => listTransactions(signal),
    // Only fetched once the parent actually asks for it.
    enabled: open,
  });

  const groups = groupByCurrency(query.data ?? []);

  return (
    <section className="mt-10" aria-labelledby="activity-heading">
      <h2 id="activity-heading" className="sr-only">
        {t('app.billing.activity')}
      </h2>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="activity-panel"
        className="inline-flex min-h-12 items-center gap-2 rounded-xl px-3 text-sm font-semibold text-burgundy-700 hover:bg-cream-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy-500"
      >
        <span aria-hidden="true" className="text-xs">
          {open ? '▾' : '▸'}
        </span>
        {open ? t('app.billing.activity_hide') : t('app.billing.activity_show')}
      </button>

      {open && (
        <div id="activity-panel" className="mt-4">
          {query.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : groups.length === 0 ? (
            <p className="text-sm text-ink-500">{t('app.billing.activity_none')}</p>
          ) : (
            <div className="space-y-6">
              {groups.map((group) => (
                <CurrencyGroup key={group.currency} currency={group.currency} rows={group.rows} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CurrencyGroup({ currency, rows }: { currency: string; rows: Transaction[] }) {
  const t = useT();
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-500">{currency}</h3>
      <div className="mt-2 overflow-x-auto rounded-2xl bg-white ring-1 ring-burgundy-500/10">
        <table className="w-full text-sm">
          <thead className="bg-cream-100 text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th scope="col" className="px-4 py-3 text-start">
                {t('app.billing.invoice_date')}
              </th>
              <th scope="col" className="px-4 py-3 text-start">
                {t('app.billing.invoice_status')}
              </th>
              <th scope="col" className="px-4 py-3 text-end">
                {t('app.billing.invoice_amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.kind}-${row.id}`} className="border-t border-burgundy-500/5">
                <td className="px-4 py-3 text-ink-700 tabular-nums">
                  {row.occurredAt.slice(0, 10)}
                </td>
                <td className="px-4 py-3 text-ink-700">
                  {row.kind === 'refund'
                    ? t('app.billing.txn_refund')
                    : t('app.billing.txn_invoice')}
                </td>
                <td className="px-4 py-3 text-end">
                  {/* `signed` renders the refund's already-negative amount as
                      −X in the burgundy accent; the API sends it negative. */}
                  <Money
                    amountMinor={row.amountMinor}
                    currency={row.currency}
                    showCurrency={false}
                    signed
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
